import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, ChevronRight, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNotificationSocket } from '../hooks/useNotificationSocket';
import { useAuth } from '../contexts/AuthContext';
import { useButtonAlertsPendingSync } from '../contexts/ButtonAlertsPendingSyncContext';
import {
  normalizeNotificationPayload,
  notificationPayloadToNum,
  pickButtonAlertId,
  pickMapNotificationId,
  type NotificationPayload,
} from '../utils/notificationPayload';
import {
  type NotificationItem,
  pendingRowToNotificationItem,
  enrichNotificationItemFromSocket,
  mapNotificationRowToItem,
  showInMapSidePanel,
  type MapNotificationPendingRow,
} from '../utils/mapNotificationItem';
import type { PendingButtonAlertRow } from '../utils/pendingButtonAlertRow';
import { isPendingButtonAlertSiteLinked } from '../utils/buttonAlertSiteLink';
import {
  applyVerifiedSiteToPayload,
  resolveMapNotificationWebVisibility,
} from '../utils/verifyButtonSiteLink';
import { ESCUDO_URBANO_ALERT_TITLE, formatRelativeTime } from '../utils/euEscudoUrbanoDisplay';
import {
  BUTTON_ALERT_COMMENT_MAX_LEN,
  BUTTON_ALERT_COMMENT_MIN_LEN
} from '../constants/buttonAlertManagement';
import AlertAudioPlayButton from './AlertAudioPlayButton';
import './Header.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const CAPTCHA_LEN = 5;
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCaptchaCode(): string {
  let s = '';
  for (let i = 0; i < CAPTCHA_LEN; i++) {
    s += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
  }
  return s;
}

/** Tras un push de mapa, la fila puede tardar en aparecer en pendientes; el subscribe general ya hace un fetch inmediato. */
const MAP_PENDING_CATCHUP_MS = [700, 2000];

export type { NotificationItem };

interface MapNotificationPanelProps {
  onMapNotificationReceived?: (notification: NotificationItem) => void;
  onFocusOnMap?: (lat: number, lng: number, fly?: boolean) => void;
  autoCenterOnMapNotification?: boolean;
  onAutoCenterOnMapNotificationChange?: (enabled: boolean) => void;
}

const MapNotificationPanel: React.FC<MapNotificationPanelProps> = ({
  onMapNotificationReceived,
  onFocusOnMap,
  autoCenterOnMapNotification = true,
  onAutoCenterOnMapNotificationChange,
}) => {
  const { subscribe, isConnected } = useNotificationSocket();
  const { token, isAuthenticated } = useAuth();
  const { pendingListGeneration, notifyPendingButtonAlertsChanged } = useButtonAlertsPendingSync();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [manageTarget, setManageTarget] = useState<NotificationItem | null>(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [manageComment, setManageComment] = useState('');
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const resyncTimeoutsRef = useRef<number[]>([]);
  const manageModalRef = useRef<HTMLDivElement>(null);

  const loadPendingAlerts = useCallback(async (options?: { silent?: boolean }) => {
    if (!token || !isAuthenticated) return;
    const silent = options?.silent === true;
    if (!silent) setPendingLoading(true);
    try {
      const [alertsRes, mapRes] = await Promise.all([
        axios.get<{ success?: boolean; data?: PendingButtonAlertRow[] }>(
          `${API_URL}/button-alerts/pending`,
          { headers: { Authorization: `Bearer ${token}` }, params: { limit: 100 } }
        ),
        axios.get<{ success?: boolean; data?: MapNotificationPendingRow[] }>(
          `${API_URL}/map-notifications/pending`,
          { headers: { Authorization: `Bearer ${token}` }, params: { limit: 100 } }
        ),
      ]);
      const rows = Array.isArray(alertsRes.data?.data) ? alertsRes.data.data : [];
      const mapRows = Array.isArray(mapRes.data?.data) ? mapRes.data.data : [];
      const buttonItems: NotificationItem[] = rows
        .filter(isPendingButtonAlertSiteLinked)
        .map(pendingRowToNotificationItem);
      const workerItems: NotificationItem[] = mapRows
        .map(mapNotificationRowToItem)
        .filter(showInMapSidePanel);
      const items = [...workerItems, ...buttonItems].sort(
        (a: NotificationItem, b: NotificationItem) => (b.receivedAt ?? 0) - (a.receivedAt ?? 0)
      );
      setNotifications(items);
    } catch {
      /* mantener lista actual si falla la red */
    } finally {
      if (!silent) setPendingLoading(false);
    }
  }, [token, isAuthenticated]);

  const scheduleMapPendingCatchUp = useCallback(() => {
    if (!token || !isAuthenticated) return;
    resyncTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
    resyncTimeoutsRef.current = MAP_PENDING_CATCHUP_MS.map((ms) =>
      window.setTimeout(() => void loadPendingAlerts({ silent: true }), ms)
    );
  }, [token, isAuthenticated, loadPendingAlerts]);

  useEffect(() => {
    return () => {
      resyncTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  useEffect(() => {
    if (token && isAuthenticated) {
      void loadPendingAlerts();
    } else {
      setNotifications([]);
    }
  }, [token, isAuthenticated, loadPendingAlerts]);

  useEffect(() => {
    const unsubscribe = subscribe<unknown>('notification', () => {
      if (token && isAuthenticated) void loadPendingAlerts({ silent: true });
    });
    return unsubscribe;
  }, [subscribe, loadPendingAlerts, token, isAuthenticated]);

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    if (pendingListGeneration === 0) return;
    void loadPendingAlerts({ silent: true });
  }, [pendingListGeneration, token, isAuthenticated, loadPendingAlerts]);

  useEffect(() => {
    const unsubscribe = subscribe<Record<string, unknown>>('notification', (raw) => {
      void (async () => {
        const base = raw && typeof raw === 'object' ? { ...raw } : {};
        const norm = normalizeNotificationPayload(raw);
        const flat = { ...base, ...(norm || {}) } as Record<string, unknown>;
        if (flat.scope !== 'map') return;

        const payloadForVerify = (norm ?? { scope: 'map' }) as NotificationPayload;
        const visibility = await resolveMapNotificationWebVisibility(
          payloadForVerify,
          raw,
          token && isAuthenticated ? token : null
        );
        if (!visibility.show) return;

        const buttonAlertId = norm?.buttonAlertId ?? pickButtonAlertId(flat);
        const mapNotificationId = norm?.mapNotificationId ?? pickMapNotificationId(flat);
        const receivedAt = Date.now();
        const baseItem: NotificationItem = {
          ...flat,
          id: mapNotificationId ?? buttonAlertId ?? receivedAt,
          receivedAt,
          scope: 'map',
          buttonAlertId: buttonAlertId ?? undefined,
          mapNotificationId: mapNotificationId ?? undefined,
          mapNotificationKind:
            norm?.mapNotificationKind != null && String(norm.mapNotificationKind).trim() !== ''
              ? String(norm.mapNotificationKind).trim()
              : undefined,
          sourceType: norm?.sourceType ?? (typeof flat.sourceType === 'string' ? flat.sourceType : undefined),
          siteId:
            visibility.verifiedSiteId ??
            norm?.siteId ??
            notificationPayloadToNum(flat.siteId),
          siteName:
            visibility.verifiedSiteName ??
            (typeof flat.siteName === 'string' ? flat.siteName : undefined),
          message: typeof flat.message === 'string' ? flat.message : undefined,
          alertType: typeof flat.alertType === 'string' ? flat.alertType : undefined,
          type: typeof flat.type === 'string' ? flat.type : undefined,
          latitude: typeof flat.latitude === 'number' ? flat.latitude : undefined,
          longitude: typeof flat.longitude === 'number' ? flat.longitude : undefined,
          sound: norm?.sound ?? notificationPayloadToNum(flat.sound),
          deviceId: norm?.deviceId ?? notificationPayloadToNum(flat.deviceId),
        };
        let item = enrichNotificationItemFromSocket(flat, norm, baseItem);
        if (visibility.verifiedSiteId != null) {
          item = applyVerifiedSiteToPayload(item, {
            verifiedSiteId: visibility.verifiedSiteId,
            verifiedSiteName: visibility.verifiedSiteName,
            verifiedSiteAddress: visibility.verifiedSiteAddress,
          }) as NotificationItem;
          if (visibility.verifiedSiteName) {
            item = {
              ...item,
              siteDisplayName: visibility.verifiedSiteName,
              siteAddress: visibility.verifiedSiteAddress ?? item.siteAddress,
            };
          }
        }

        if (!showInMapSidePanel(item)) {
          if (token && isAuthenticated) void loadPendingAlerts({ silent: true });
          return;
        }

        if (token && isAuthenticated) {
          scheduleMapPendingCatchUp();
        } else {
          setNotifications((prev) => {
            if (item.mapNotificationId != null) {
              const rest = prev.filter((x) => x.mapNotificationId !== item.mapNotificationId);
              return [item, ...rest].slice(0, 50);
            }
            if (item.buttonAlertId != null) {
              const rest = prev.filter((x) => x.buttonAlertId !== item.buttonAlertId);
              return [item, ...rest].slice(0, 50);
            }
            return [item, ...prev].slice(0, 50);
          });
        }

        if (typeof item.latitude === 'number' && typeof item.longitude === 'number' && onMapNotificationReceived) {
          onMapNotificationReceived(item);
        } else if (onFocusOnMap && typeof item.latitude === 'number' && typeof item.longitude === 'number') {
          onFocusOnMap(item.latitude, item.longitude, autoCenterOnMapNotification);
        }
      })();
    });
    return unsubscribe;
  }, [
    subscribe,
    onMapNotificationReceived,
    onFocusOnMap,
    autoCenterOnMapNotification,
    token,
    isAuthenticated,
    scheduleMapPendingCatchUp,
    loadPendingAlerts,
  ]);

  const openManageModal = useCallback((n: NotificationItem) => {
    setCaptchaCode(randomCaptchaCode());
    setCaptchaInput('');
    setManageComment('');
    setManageTarget(n);
  }, []);

  const closeManageModal = useCallback(() => {
    setManageTarget(null);
    setCaptchaInput('');
    setManageComment('');
    setCaptchaCode('');
  }, []);

  useEffect(() => {
    if (!manageTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeManageModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [manageTarget, closeManageModal]);

  useEffect(() => {
    if (!manageTarget) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (manageModalRef.current?.contains(t)) return;
      closeManageModal();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [manageTarget, closeManageModal]);

  const trimmedManageComment = manageComment.trim();
  const captchaOk = captchaInput.length === CAPTCHA_LEN && captchaInput === captchaCode;
  const commentOk =
    trimmedManageComment.length >= BUTTON_ALERT_COMMENT_MIN_LEN &&
    trimmedManageComment.length <= BUTTON_ALERT_COMMENT_MAX_LEN;
  const canConfirmManage = captchaOk && commentOk && !manageSubmitting;

  const confirmManage = useCallback(async () => {
    if (!manageTarget || !token) return;
    const commentText = manageComment.trim();
    if (
      captchaInput !== captchaCode ||
      commentText.length < BUTTON_ALERT_COMMENT_MIN_LEN ||
      commentText.length > BUTTON_ALERT_COMMENT_MAX_LEN
    ) {
      return;
    }
    const alertId = manageTarget.buttonAlertId ?? manageTarget.id;
    setManageSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/button-alerts/${alertId}/acknowledge`,
        { comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alarma gestionada');
      setNotifications((prev) => prev.filter((x) => (x.buttonAlertId ?? x.id) !== alertId));
      notifyPendingButtonAlertsChanged();
      closeManageModal();
      void loadPendingAlerts({ silent: true });
    } catch (e) {
      console.warn('[MapNotificationPanel] acknowledge', e);
      toast.error('No se pudo registrar la gestión');
    } finally {
      setManageSubmitting(false);
    }
  }, [
    manageTarget,
    token,
    captchaInput,
    captchaCode,
    manageComment,
    closeManageModal,
    loadPendingAlerts,
    notifyPendingButtonAlertsChanged,
  ]);

  const manageCard =
    manageTarget != null
      ? {
          siteName: manageTarget.siteDisplayName ?? manageTarget.siteName ?? '—',
          address: manageTarget.siteAddress ?? '—',
          alarmLabel: manageTarget.alarmLabelDisplay ?? '—',
          when:
            formatRelativeTime(manageTarget.timestampIso) ??
            (manageTarget.receivedAt != null
              ? formatRelativeTime(new Date(manageTarget.receivedAt).toISOString())
              : null),
          pressCount:
            typeof manageTarget.pressCount === 'number' && manageTarget.pressCount >= 1
              ? manageTarget.pressCount
              : 1,
        }
      : null;

  const goToPoint = useCallback(
    (n: NotificationItem) => {
      if (onMapNotificationReceived) {
        onMapNotificationReceived(n);
        return;
      }
      if (onFocusOnMap && typeof n.latitude === 'number' && typeof n.longitude === 'number') {
        onFocusOnMap(n.latitude, n.longitude, true);
      }
    },
    [onMapNotificationReceived, onFocusOnMap]
  );

  const acknowledgeWorker = useCallback(
    async (n: NotificationItem) => {
      const id = n.mapNotificationId;
      if (id == null || !token) return;
      try {
        await axios.patch(
          `${API_URL}/map-notifications/${id}/acknowledge`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Registrado');
        setNotifications((prev) => prev.filter((x) => x.mapNotificationId !== id));
        void loadPendingAlerts({ silent: true });
      } catch (e) {
        console.warn('[MapNotificationPanel] acknowledge worker', e);
        toast.error('No se pudo registrar');
      }
    },
    [token, loadPendingAlerts]
  );

  const goToStreams = useCallback(
    (n: NotificationItem) => {
      const siteId = n.siteId != null ? Number(n.siteId) : undefined;
      const siteName = typeof n.siteName === 'string' ? n.siteName : undefined;
      navigate('/streams', {
        state: {
          siteId,
          siteName,
          fromMap: true,
          mapSessionId: Date.now(),
          buttonAlertId: n.buttonAlertId,
          latitude: typeof n.latitude === 'number' ? n.latitude : undefined,
          longitude: typeof n.longitude === 'number' ? n.longitude : undefined,
          deviceId: n.deviceId,
        },
      });
    },
    [navigate]
  );

  return (
    <div className={`map-notification-panel ${collapsed ? 'map-notification-panel--collapsed' : ''}`}>
      <button
        type="button"
        className="map-notification-panel__toggle"
        onClick={() => setCollapsed((c) => !c)}
        title={collapsed ? 'Abrir notificaciones' : 'Colapsar'}
        aria-label={collapsed ? 'Abrir notificaciones' : 'Colapsar'}
      >
        {collapsed ? (
          <>
            <Bell size={20} />
            {notifications.length > 0 && (
              <span className="map-notification-panel__badge">{notifications.length}</span>
            )}
          </>
        ) : (
          <ChevronRight size={20} />
        )}
      </button>

      <div className="map-notification-panel__content">
        <div className="map-notification-panel__header">
          <h3>
            Notificaciones
            {notifications.length > 0 && (
              <span className="map-notification-panel__count">({notifications.length})</span>
            )}
            {pendingLoading && <span className="map-notification-panel__pending-loading"> …</span>}
          </h3>
          {isConnected && (
            <span className="map-notification-panel__status" title="Conectado al socket">
              ●
            </span>
          )}
          <button
            type="button"
            className="map-notification-panel__collapse"
            onClick={() => setCollapsed(true)}
            title="Colapsar"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        {onAutoCenterOnMapNotificationChange && (
          <div className="map-notification-panel__switch-row">
            <label className="map-notification-panel__switch-label">
              <input
                type="checkbox"
                className="map-notification-panel__switch"
                checked={autoCenterOnMapNotification}
                onChange={(e) => onAutoCenterOnMapNotificationChange(e.target.checked)}
              />
              <span className="map-notification-panel__switch-slider" />
              <span className="map-notification-panel__switch-text">Centrar mapa con alertas</span>
            </label>
          </div>
        )}
        <div className="map-notification-panel__list">
          {notifications.length === 0 && pendingLoading ? (
            <p className="map-notification-panel__empty">Cargando alertas…</p>
          ) : notifications.length === 0 ? (
            <p className="map-notification-panel__empty">
              {isAuthenticated ? 'No hay alertas pendientes' : 'Sin alertas de mapa recientes'}
            </p>
          ) : (
            notifications.map((n) => {
              const isWorker = n.mapNotificationId != null && n.buttonAlertId == null;
              const hasCoords = typeof n.latitude === 'number' && typeof n.longitude === 'number';
              const canGoPoint = onMapNotificationReceived || (onFocusOnMap && hasCoords);
              const canStreams = n.siteId != null || (n.siteName && String(n.siteName).trim() !== '');
              const canGestionar = n.buttonAlertId != null && !!token;
              const rowKey =
                n.mapNotificationId != null
                  ? `mn-${n.mapNotificationId}`
                  : n.buttonAlertId != null
                    ? `ba-${n.buttonAlertId}`
                    : `id-${n.id}`;
              const when =
                formatRelativeTime(n.timestampIso) ??
                (n.receivedAt != null ? formatRelativeTime(new Date(n.receivedAt).toISOString()) : null);
              const siteLine = n.siteDisplayName ?? n.siteName ?? '—';
              const addressLine = n.siteAddress ?? '—';
              const alarmLine = n.alarmLabelDisplay ?? '—';
              const dateTimeAttr =
                n.timestampIso ??
                (n.receivedAt != null ? new Date(n.receivedAt).toISOString() : undefined);
              const pressDisplay =
                typeof n.pressCount === 'number' && n.pressCount >= 1 ? n.pressCount : 1;

              if (isWorker) {
                const workerTitle =
                  typeof n.notificationTitle === 'string' && n.notificationTitle.trim() !== ''
                    ? n.notificationTitle
                    : n.mapNotificationKind === 'scene_monitor'
                      ? 'Análisis de escena'
                      : n.mapNotificationKind === 'system_state'
                        ? 'Cambio de estado'
                        : (n.mapNotificationKind ?? 'Aviso');
                return (
                  <div key={rowKey} className="map-notification-panel__worker-item">
                    <div className="map-notification-panel__worker-head">
                      <h4 className="map-notification-panel__worker-title">{workerTitle}</h4>
                      {when && dateTimeAttr && (
                        <time className="map-notification-panel__worker-when" dateTime={dateTimeAttr}>
                          {when}
                        </time>
                      )}
                    </div>
                    {addressLine !== '—' && (
                      <p className="map-notification-panel__worker-address">{addressLine}</p>
                    )}
                    <p className="map-notification-panel__worker-msg">{n.message ?? '—'}</p>
                    <button
                      type="button"
                      className="map-notification-panel__worker-ack"
                      disabled={!token}
                      title={!token ? 'Inicia sesión para confirmar' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        void acknowledgeWorker(n);
                      }}
                    >
                      Entendido
                    </button>
                  </div>
                );
              }

              return (
                <div
                  key={rowKey}
                  className="header-notifications-dropdown__item map-notification-panel__eu-item"
                >
                  <div className="header-notifications-dropdown__item-grid">
                    <AlertAudioPlayButton
                      buttonAlertId={n.buttonAlertId}
                      token={token}
                      sound={n.sound}
                      size={14}
                    />
                    <div className="header-notifications-dropdown__item-content">
                      <div className="header-notifications-dropdown__item-headline">
                        <h3 className="header-notifications-dropdown__item-title-eu">{ESCUDO_URBANO_ALERT_TITLE}</h3>
                        {when && dateTimeAttr && (
                          <time className="header-notifications-dropdown__item-when" dateTime={dateTimeAttr}>
                            {when}
                          </time>
                        )}
                      </div>
                      <div className="header-notifications-dropdown__item-block">
                        <span className="header-notifications-dropdown__prov-label">Ubicación</span>
                        <p className="header-notifications-dropdown__item-site-name">{siteLine}</p>
                        <span className="header-notifications-dropdown__prov-label">Dirección</span>
                        <p className="header-notifications-dropdown__item-address">{addressLine}</p>
                      </div>
                      <div className="header-notifications-dropdown__item-block">
                        <span className="header-notifications-dropdown__prov-label">Tipo de alarma</span>
                        <p className="header-notifications-dropdown__item-alarm-type">{alarmLine}</p>
                      </div>
                      <div className="header-notifications-dropdown__item-block">
                        <span className="header-notifications-dropdown__prov-label">Pulsaciones del botón</span>
                        <p className="header-notifications-dropdown__item-alarm-type map-alert-press-count">
                          {pressDisplay}×
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="map-notification-panel__map-actions">
                    <button
                      type="button"
                      className="map-notification-panel__action-btn"
                      disabled={!canGoPoint}
                      title={!canGoPoint ? 'Sin coordenadas ni enlace al mapa' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToPoint(n);
                      }}
                    >
                      Ir al punto
                    </button>
                    <button
                      type="button"
                      className="map-notification-panel__action-btn"
                      disabled={!canStreams}
                      title={!canStreams ? 'Sin sitio asociado' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToStreams(n);
                      }}
                    >
                      Ver stream
                    </button>
                    <button
                      type="button"
                      className="map-notification-panel__action-btn map-notification-panel__action-btn--primary"
                      disabled={!canGestionar || (manageSubmitting && (manageTarget?.buttonAlertId ?? manageTarget?.id) === n.buttonAlertId)}
                      title={
                        n.buttonAlertId == null
                          ? 'Esta alerta no tiene id en base de datos'
                          : !token
                            ? 'Inicia sesión para gestionar'
                            : undefined
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        openManageModal(n);
                      }}
                    >
                      Gestionar alarma
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {manageTarget &&
        createPortal(
          <div
            ref={manageModalRef}
            className="header-alerts-manage-modal__backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeManageModal();
            }}
          >
            <div
              className="header-alerts-manage-modal"
              role="dialog"
              aria-labelledby="map-panel-alerts-manage-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="header-alerts-manage-modal__header">
                <ShieldCheck size={22} className="header-alerts-manage-modal__header-icon" aria-hidden />
                <h2 id="map-panel-alerts-manage-title" className="header-alerts-manage-modal__title">
                  Confirmar gestión
                </h2>
                <p className="header-alerts-manage-modal__subtitle">
                  Completa la verificación y un comentario para registrar que atendiste esta alerta.
                </p>
              </div>
              <div className="header-alerts-manage-modal__context">
                {manageCard && manageTarget && (
                  <>
                    <div className="header-alerts-manage-modal__context-headline">
                      <span className="header-alerts-manage-modal__context-kind">{ESCUDO_URBANO_ALERT_TITLE}</span>
                      {manageCard.when && (
                        <span className="header-alerts-manage-modal__context-when">{manageCard.when}</span>
                      )}
                    </div>
                    <span className="header-alerts-manage-modal__context-sublabel">Ubicación</span>
                    <span className="header-alerts-manage-modal__context-value">{manageCard.siteName}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Dirección</span>
                    <span className="header-alerts-manage-modal__context-value">{manageCard.address}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Tipo de alarma</span>
                    <span className="header-alerts-manage-modal__context-alarm">{manageCard.alarmLabel}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Pulsaciones del botón</span>
                    <span className="header-alerts-manage-modal__context-alarm map-alert-press-count">
                      {manageCard.pressCount}×
                    </span>
                    <span className="header-alerts-manage-modal__context-sublabel">Mensaje</span>
                    <span className="header-alerts-manage-modal__context-msg">
                      {typeof manageTarget.message === 'string' && manageTarget.message.trim() !== ''
                        ? manageTarget.message
                        : '—'}
                    </span>
                  </>
                )}
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="map-panel-alerts-captcha">
                  Código de verificación <span className="header-alerts-manage-modal__hint">(copia el código)</span>
                </label>
                <div className="header-alerts-manage-modal__captcha-row" aria-hidden>
                  {captchaCode.split('').map((ch, i) => (
                    <span key={`${captchaCode}-${ch}-${i}`} className="header-alerts-manage-modal__captcha-char">
                      {ch}
                    </span>
                  ))}
                </div>
                <input
                  id="map-panel-alerts-captcha"
                  className="header-alerts-manage-modal__input"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={CAPTCHA_LEN}
                  placeholder={`Escribe los ${CAPTCHA_LEN} caracteres`}
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                />
                {captchaInput.length > 0 && !captchaOk && (
                  <span className="header-alerts-manage-modal__error">El código no coincide.</span>
                )}
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="map-panel-alerts-comment">
                  Comentario de gestión{' '}
                  <span className="header-alerts-manage-modal__hint">
                    (mín. {BUTTON_ALERT_COMMENT_MIN_LEN}, máx. {BUTTON_ALERT_COMMENT_MAX_LEN} caracteres)
                  </span>
                </label>
                <textarea
                  id="map-panel-alerts-comment"
                  className="header-alerts-manage-modal__textarea"
                  rows={4}
                  maxLength={BUTTON_ALERT_COMMENT_MAX_LEN}
                  placeholder="Describe brevemente la acción tomada…"
                  value={manageComment}
                  onChange={(e) => setManageComment(e.target.value)}
                />
                <span className="header-alerts-manage-modal__counter">
                  {trimmedManageComment.length} / {BUTTON_ALERT_COMMENT_MAX_LEN} · mín.{' '}
                  {BUTTON_ALERT_COMMENT_MIN_LEN}
                </span>
              </div>
              <div className="header-alerts-manage-modal__actions">
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--ghost"
                  onClick={closeManageModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--primary"
                  disabled={!canConfirmManage}
                  onClick={() => void confirmManage()}
                >
                  {manageSubmitting ? 'Guardando…' : 'Confirmar gestión'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MapNotificationPanel;
